import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

type SidebarStackContextValue = {
  openLeadIds: ReadonlySet<number>;
  openDealIds: ReadonlySet<number>;
  registerLead: (id: number) => void;
  unregisterLead: (id: number) => void;
  registerDeal: (id: number) => void;
  unregisterDeal: (id: number) => void;
  isLeadOpen: (id: number) => boolean;
  isDealOpen: (id: number) => boolean;
};

const SidebarStackContext = createContext<SidebarStackContextValue | undefined>(undefined);

export const SidebarStackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [openLeadIds, setOpenLeadIds] = useState<Set<number>>(() => new Set());
  const [openDealIds, setOpenDealIds] = useState<Set<number>>(() => new Set());

  const registerLead = useCallback((id: number) => {
    setOpenLeadIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const unregisterLead = useCallback((id: number) => {
    setOpenLeadIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const registerDeal = useCallback((id: number) => {
    setOpenDealIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const unregisterDeal = useCallback((id: number) => {
    setOpenDealIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const value = useMemo<SidebarStackContextValue>(
    () => ({
      openLeadIds,
      openDealIds,
      registerLead,
      unregisterLead,
      registerDeal,
      unregisterDeal,
      isLeadOpen: (id: number) => openLeadIds.has(id),
      isDealOpen: (id: number) => openDealIds.has(id),
    }),
    [openLeadIds, openDealIds, registerLead, unregisterLead, registerDeal, unregisterDeal]
  );

  return <SidebarStackContext.Provider value={value}>{children}</SidebarStackContext.Provider>;
};

export const useSidebarStack = (): SidebarStackContextValue => {
  const ctx = useContext(SidebarStackContext);
  if (!ctx) {
    throw new Error("useSidebarStack must be used within a SidebarStackProvider");
  }
  return ctx;
};
